import axios from 'axios';
import qs from 'qs';
import RequestQueue from './RequestQueue';
import TokenManager from './TokenManager';
import DataSet from './DataSet';
import UndoStack from './UndoStack';
import RedoStack from './RedoStack';
import { Mutex } from 'await-semaphore';
const strftime = require('strftime');

const beginDay = (period) => {
	let ym = new Date();
	ym.setDate(ym.getDate() - period.offset);
	return strftime('%Y%m%d', ym);
};
const endDay = (period) => {
	let ym = new Date();
	ym.setDate(ym.getDate() + period.default);
	return strftime('%Y%m%d', ym);
};
const MUTEX = {
	access_token: new Mutex(),
	refresh_token: new Mutex(),
};
export default class DataSetManager {
	constructor(store) {
		this.$store = store;
		this.queue = {
			request: new RequestQueue(store),
		};
		this.last_dataset_id = null;
		this.dataset = {
			before: null,
			after: null,
		};
		this.stack = {
			undo: new UndoStack(5),
			redo: new RedoStack(5),
		};
	}

	reset() {
		this.queue.request.clear();
		this.last_dataset_id = null;
		this.dataset.before = null;
		this.dataset.after = null;
		this.stack.undo.clear();
		this.stack.redo.clear();
	}

	get canUndo() {
		return this.stack.undo.length > 0;
	}
	get canRedo() {
		return this.stack.redo.length > 0;
	}

	/* fetch_for_memberと日付省略時はインターフェースは同じにする */
	fetch(access_token, begin, end) {
		if (begin === undefined)
			begin = beginDay(this.$store.getters.displayPeriod);
		if (end === undefined)
			end = endDay(this.$store.getters.displayPeriod);

		return axios.get('/api/v1/dataset', {
			params: {
				'contractor_id': access_token.iss,
				'begin_day': begin,
				'end_day': end,
			},
			headers: {
				Authorization: `Bearer ${access_token.token}`,
			},
		}).then((res) => {
			this.stack.undo.clear();
			this.stack.redo.clear();
			this.last_dataset_id = res.data.dataset_id;
			
			return res.data;
		});
	}

	/* fetchの日付省略時とインターフェースは同じにする */
	fetch_for_member(access_token) {
		return axios.get('/api/v1/dataset/member', {
			params: {
				'contractor_id': access_token.iss,
			},
			headers: {
				Authorization: `Bearer ${access_token.token}`,
			},
		}).then((res) => {
			this.stack.undo.clear();
			this.stack.redo.clear();
			this.last_dataset_id = res.data.dataset_id;
			
			return res.data;
		});
	}
	
	setBeforeDataset() {
		this.dataset.before = DataSet.createInstance(this.$store);
	}
	
	setAfterDataset() {
		this.dataset.after = DataSet.createInstance(this.$store);
	}
	
	sync(done, fail, token_fail, opt) {
		this.stack.redo.clear();
		if (this.dataset.before)
			this.stack.undo.push(this.dataset.before);
		else
			this.stack.undo.clear();
		this._sync(this.dataset.after, this.dataset.before, done, fail, token_fail, opt);
		this.dataset.after = null;
		this.dataset.before = null;
	}

	syncMember(done, fail, token_fail, opt) {
		if (opt === undefined)
			opt = {};
		opt.member_only = true;

		this.sync(done, fail, token_fail, opt);
	}

	syncProgressRate(done, fail, token_fail, opt) {
		if (opt === undefined)
			opt = {};
		opt.progress_rate_only = true;

		this.sync(done, fail, token_fail, opt);
	}
	
	syncTitle(done, fail, token_fail, opt) {
		if (opt === undefined)
			opt = {};
		opt.title_only = true;

		this.sync(done, fail, token_fail, opt);
	}
		
	syncColor(done, fail, token_fail, opt) {
		if (opt === undefined)
			opt = {};
		opt.color_only = true;

		this.sync(done, fail, token_fail, opt);
	}
	
	syncProjectPinned(done, fail, token_fail, opt) {
		if (opt === undefined)
			opt = {};
		opt.pinned_only = true;

		this.sync(done, fail, token_fail, opt);
	}
	
	async validateAccessToken (access_token, refresh_token) {
		return MUTEX.access_token.use( async () => {
			if (!TokenManager.isExpiredAccessToken(access_token))
				return access_token;
			
			if (TokenManager.isExpiredRefreshToken(refresh_token))
				return null;
			
			refresh_token = await this.validateRefreshToken(refresh_token)
				.catch(() => {
					return null;
				});
			if(!refresh_token) {
				return null;
			}
			await this.$store.dispatch('setRefreshToken', refresh_token);
			
			access_token = await TokenManager.accessTokenRequest(refresh_token.token)
				.catch(() => {
					return null;
				});
			if (!access_token) {
				return null;
			}
			await this.$store.dispatch('setAccessToken', access_token);
			return access_token;
		});
		
	}
	
	async validateRefreshToken (refresh_token) {
		return MUTEX.refresh_token.use( async () => {
			if (!TokenManager.isExpireSoonRefreshToken(refresh_token))
				return refresh_token
			
			return await TokenManager.refreshTokenRequest(refresh_token.token);
		});
	}
	
	async getConoHaUserToken () {
		let access_token = this.$store.getters.accessToken;
		let refresh_token = this.$store.getters.refreshToken;
		access_token = await this.validateAccessToken (access_token, refresh_token);
		if (access_token) {
			let contractor_id = this.$store.getters.accessToken.iss;
			return await axios
				.get('/api/v1/conoha/' + contractor_id + '/token', {
					headers: {
						Authorization: `Bearer ${access_token.token}`,
					},
				}).then((res) => {
					return res.data;
				}).catch(() => {
					return null;
				});
		} else {
			return null;
		}
	}
	
	_sync(after, before, done, fail, token_fail, opt) {
		const onDone = (res) => {
			if (before) {
				before.sync();
				this.stack.undo.pool.forEach(s => {
					if(s.request_id === before.request_id) {
						s.sync();
					}
				});
				this.stack.redo.pool.forEach(s => {
					if(s.request_id === before.request_id) {
						s.sync();
					}
				});
			}
			after.sync();
			this.last_dataset_id = res.data.dataset_id;
			this.$store.commit('updateDataSetID', res.data.dataset_id);
			if (done)
				done(res);
		};
		const onFail = async (err) => {
			this.queue.request.clear();
			this.stack.redo.clear();
			this.stack.undo.clear();
			if (fail)
				fail(err);
		};
		const onToPromise = async (req) => {
			let access_token = this.$store.getters.accessToken;
			let refresh_token = this.$store.getters.refreshToken;
			if (await this.validateAccessToken (access_token, refresh_token)) {
				let req_data = qs.parse(req.params.data);
				let dataset = JSON.parse(req_data.dataset);
				
				dataset.dataset_id = this.last_dataset_id;
				req_data.dataset = JSON.stringify(dataset);
				req.params.data = qs.stringify(req_data);
				req.params.headers.Authorization = 'Bearer ' + this.$store.getters.accessToken.token;
				
				return req;
			} else {
				this.queue.request.clear();
				if(token_fail)
					token_fail();
				
				return null;
			}
		}; 

		after.reset();
		let req;
		if (opt && opt.progress_rate_only)
			req = after.createUpdateProgressRateRequest(onDone, onFail, onToPromise)
		else if (opt && opt.member_only)
			req = after.createUpdateMemberRequest(onDone, onFail, onToPromise)
		else if (opt && opt.title_only)
			req = after.createUpdateTitleRequest(onDone, onFail, onToPromise);
		else if (opt && opt.color_only)
			req = after.createUpdateColorRequest(onDone, onFail, onToPromise);
		else if (opt && opt.pinned_only)
			req =  after.createUpdateProjectPinnedRequest(onDone, onFail, onToPromise);
		else
			req =  after.createUpdateRequest(onDone, onFail, onToPromise);
		if (before)
			before.request_id = req.request_id;
		this.queue.request.enqueue(req);
	}
	
	async updateDataSet (opt) {
		if (opt === undefined)
			opt = {
				default_period: false,
				period: null,
				force_update: false,
				for_member : false,
				callback: null,
			};

		let access_token = await DataSetManager.confirmAccessToken(this.$store);
		if (!access_token) {
			throw {
				token: 'can not set access token.',
			};
		}
		
		if (opt.for_member) {
			return await this.fetch_for_member(access_token)
				.then((data) => {
					this.$store.commit('createData', data);
				});
		}

		let list_calendar = this.$store.getters.listCalendar;
		let begin, end;
		if (opt.default_period) {
			begin = undefined;
			end = undefined;
		} else if (opt.period) {
			begin = opt.period.begin;
			end = opt.period.end;
		} else {
			begin = list_calendar[0].code;
			end = list_calendar[list_calendar.length - 1].code;
		}
		
		return await this.fetch(access_token, begin, end)
			.then((data) => {
				if (opt.force_update || (data.dataset_id !== this.$store.getters.datasetID)) {
					this.$store.commit('createData', data);
				}
				if (opt.callback)
					opt.callback();
			});
	}
	
	static async confirmAccessToken (store) {
		return MUTEX.access_token.use( async () => {
			let access_token = store.getters.accessToken;
			if (!TokenManager.isExpiredAccessToken(access_token))
				return access_token;
			
			let refresh_token = store.getters.refreshToken;
			if (TokenManager.isExpiredRefreshToken(refresh_token))
				return null;
			
			access_token = await TokenManager.accessTokenRequest(refresh_token.token)
				.catch(() => {
					return null;
				});
				
			if (!access_token)
				return null;
			
			if (!access_token.pms)
				return null;
			
			await store.dispatch('setAccessToken', access_token);
			
			return access_token;
		});
	}
	
	undo(done, fail, token_fail) {
		let next = this.stack.undo.pop();
		if (!next)
			return;
		
		let current = DataSet.createInstance(this.$store);
		this.stack.redo.push(current);

		let aborted = this.queue.request.abort(next.request_id);
		if (!aborted || next.synced) {
			this.$store.commit('createData', JSON.parse(next.json));
			let dataset = JSON.parse(next.json);
			this.$store.dispatch('setUpdate', dataset);
			next.json = JSON.stringify(dataset);
			this._sync(next, current, done, fail, token_fail);
		} else {
			this.$store.commit('createData', JSON.parse(next.json));
		}
	}

	redo(done, fail, token_fail) {
		let next = this.stack.redo.pop();
		if (!next)
			return;
		
		let current = DataSet.createInstance(this.$store);
		this.stack.undo.push(current);
		
		this.$store.commit('createData', JSON.parse(next.json));
		let dataset = JSON.parse(next.json);
		this.$store.dispatch('setUpdate', dataset);
		next.json = JSON.stringify(dataset);
		this._sync(next, current, done, fail, token_fail);
	}
}
